import React, { useEffect, useState } from "react";
import { useApp } from "@/lib/store";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
    Loader2, Globe, Instagram, Facebook, Linkedin, MapPinned,
    CheckCircle2, XCircle, AlertTriangle, RefreshCw, Edit3,
    ExternalLink, Sparkles, ShieldCheck, Zap, Plug,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const PLATFORM_META = {
    website: { label: "Website", icon: Globe, kind: "website" },
    google_business: { label: "Google Business Profile", icon: MapPinned, kind: "google_business" },
    instagram: { label: "Instagram", icon: Instagram, kind: "instagram" },
    facebook: { label: "Facebook", icon: Facebook, kind: "facebook" },
    linkedin: { label: "LinkedIn", icon: Linkedin, kind: "linkedin" },
};

const STATUS_ICONS = {
    ok: <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />,
    missing: <XCircle className="h-3.5 w-3.5 text-rose-300" />,
    weak: <AlertTriangle className="h-3.5 w-3.5 text-amber-300" />,
    inconsistent: <AlertTriangle className="h-3.5 w-3.5 text-amber-300" />,
};

export default function CompanyProfile() {
    const { currentBusiness } = useApp();
    const nav = useNavigate();
    const [profile, setProfile] = useState(null);
    const [completeness, setCompleteness] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [verifying, setVerifying] = useState(null);
    const [rediscoverBusy, setRediscoverBusy] = useState(false);
    const [proposals, setProposals] = useState(null);
    const [fixBusy, setFixBusy] = useState(null);

    const load = async () => {
        if (!currentBusiness) return;
        setLoading(true);
        try {
            const [p, c] = await Promise.all([
                api.companyProfile(currentBusiness.id),
                api.profileCompleteness(currentBusiness.id),
            ]);
            setProfile(p);
            setCompleteness(c);
        } catch {
            toast.error("Couldn't load profile.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [currentBusiness?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveEdit = async () => {
        try {
            await api.updateProfileField(currentBusiness.id,
                editing.field, editing.value?.trim() || null);
            toast.success("Saved.");
            setEditing(null);
            load();
        } catch {
            toast.error("Save failed.");
        }
    };

    const verifyPlatform = async (platform) => {
        setVerifying(platform);
        try {
            const r = await api.verifyProfileField(currentBusiness.id, platform);
            if (r.reachable) {
                toast.success(`${platform} is reachable (HTTP ${r.status_code}).`);
            } else if (r.status === "no_url") {
                toast.info("No URL on file for this platform.");
            } else {
                toast.error(`Unreachable (HTTP ${r.status_code}).`);
            }
            load();
        } finally {
            setVerifying(null);
        }
    };

    const runRediscover = async () => {
        setRediscoverBusy(true);
        try {
            const r = await api.profileRediscover(currentBusiness.id);
            setProposals(r);
            if ((r.proposed_changes || []).length === 0) {
                toast.info("No proposed changes — profile is already up to date.");
            }
        } catch {
            toast.error("Rediscover failed.");
        } finally {
            setRediscoverBusy(false);
        }
    };

    const acceptProposal = async (p) => {
        try {
            await api.acceptRediscoverChange(currentBusiness.id, p.field, p.proposed);
            toast.success(`Applied ${p.platform}.`);
            setProposals((s) => ({
                ...s,
                proposed_changes: (s.proposed_changes || []).filter(x => x.field !== p.field),
            }));
            load();
        } catch {
            toast.error("Apply failed.");
        }
    };

    const fixWithAI = async (platform, item, fix_kind) => {
        const key = `${platform}-${fix_kind}`;
        setFixBusy(key);
        try {
            await api.profileFixWithAI(currentBusiness.id, platform, item, fix_kind);
            toast.success("AI draft sent to Approvals inbox.");
        } catch {
            toast.error("Fix-with-AI failed.");
        } finally {
            setFixBusy(null);
        }
    };

    if (loading || !profile) {
        return (
            <div className="max-w-6xl mx-auto py-16 text-center">
                <Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" />
            </div>
        );
    }

    const biz = profile.business || {};
    return (
        <div className="max-w-6xl mx-auto space-y-6 fade-in" data-testid="company-profile-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow text-cyan-300">Company Profile</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-slate-50">{biz.name}</h1>
                    <p className="mt-2 text-sm text-slate-400">
                        Online presence · Discovered → Confirmed → Connected
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        data-testid="view-presence-btn"
                        onClick={() => nav("/presence")}
                        className="bg-indigo-500 hover:bg-indigo-400 text-white gap-2"
                    >
                        <Sparkles className="h-4 w-4" /> Presence analysis
                    </Button>
                    <Button
                        data-testid="rediscover-btn"
                        onClick={runRediscover}
                        disabled={rediscoverBusy}
                        variant="outline"
                        className="border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-slate-200 gap-2"
                    >
                        {rediscoverBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        Re-run discovery
                    </Button>
                </div>
            </div>

            <Tabs defaultValue="presence">
                <TabsList className="bg-white/[0.03] border border-white/10">
                    <TabsTrigger value="presence" data-testid="profile-tab-presence">Online Presence</TabsTrigger>
                    <TabsTrigger value="completeness" data-testid="profile-tab-completeness">Profile Completeness</TabsTrigger>
                </TabsList>

                <TabsContent value="presence" className="mt-5 space-y-3">
                    {(profile.platforms || []).map((p) => {
                        const meta = PLATFORM_META[p.platform] || {};
                        const Icon = meta.icon || Globe;
                        return (
                            <div
                                key={p.platform}
                                data-testid={`presence-${p.platform}`}
                                className="surface rounded-2xl p-5"
                            >
                                <div className="flex items-start gap-4 flex-wrap">
                                    <div className="h-10 w-10 rounded-xl bg-white/[0.03] border border-white/5 flex items-center justify-center">
                                        <Icon className="h-4 w-4 text-slate-300" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-bold text-slate-100">{meta.label}</span>
                                            <TriState label="Discovered" active={p.discovered} testid={`tri-discovered-${p.platform}`} />
                                            <TriState label="Confirmed" active={p.confirmed} testid={`tri-confirmed-${p.platform}`} />
                                            <TriState label="Connected" active={p.connected} testid={`tri-connected-${p.platform}`} />
                                            {p.confidence && (
                                                <Badge variant="outline" className="border-white/10 text-[10px]">
                                                    {p.confidence}
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="mt-1 text-xs text-slate-400 break-all" data-testid={`presence-value-${p.platform}`}>
                                            {p.value ? (
                                                <a href={p.value.startsWith("http") ? p.value : "#"} target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline inline-flex items-center gap-1">
                                                    {p.value} <ExternalLink className="h-3 w-3" />
                                                </a>
                                            ) : (
                                                <span className="italic text-slate-500">Not set</span>
                                            )}
                                        </div>
                                        {p.reason && (
                                            <div className="mt-1 text-[11px] text-slate-500">{p.reason}</div>
                                        )}
                                        {p.last_verified_at && (
                                            <div className="mt-1 text-[10px] mono text-slate-500">
                                                Last verified: {new Date(p.last_verified_at).toLocaleString()}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            data-testid={`edit-${p.platform}-btn`}
                                            onClick={() => setEditing({ field: p.field, value: p.value || "" })}
                                            className="h-8 px-2 border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-slate-200"
                                        >
                                            <Edit3 className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            data-testid={`verify-${p.platform}-btn`}
                                            disabled={!p.value || verifying === p.platform}
                                            onClick={() => verifyPlatform(p.platform)}
                                            className="h-8 px-2 border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-100 disabled:opacity-30"
                                        >
                                            {verifying === p.platform ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </TabsContent>

                <TabsContent value="completeness" className="mt-5 space-y-4">
                    <div className="surface rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3" data-testid="completeness-summary">
                        <div>
                            <div className="label-eyebrow text-emerald-300">Overall completeness</div>
                            <div className="mt-1 text-3xl font-extrabold text-slate-50">
                                {completeness?.score ?? 0}%
                            </div>
                            <div className="text-xs text-slate-500">
                                {completeness?.passing_checks} / {completeness?.total_checks} checks pass
                            </div>
                        </div>
                        <Zap className="h-8 w-8 text-emerald-300/60" />
                    </div>
                    {(completeness?.platforms || []).map((p) => {
                        const meta = PLATFORM_META[p.platform] || {};
                        const Icon = meta.icon || Globe;
                        return (
                            <div
                                key={p.platform}
                                data-testid={`completeness-${p.platform}`}
                                className="surface rounded-2xl p-5"
                            >
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="h-9 w-9 rounded-xl bg-white/[0.03] border border-white/5 flex items-center justify-center">
                                        <Icon className="h-4 w-4 text-slate-300" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-sm font-bold text-slate-100">{p.label}</div>
                                        <div className="text-[10px] mono uppercase text-slate-500">
                                            {p.connected ? "connected via oauth" : "not connected"}
                                        </div>
                                    </div>
                                </div>
                                <ul className="divide-y divide-white/5">
                                    {p.checks.map((c, i) => {
                                        const key = `${p.platform}-${c.fix_kind || i}`;
                                        return (
                                            <li
                                                key={i}
                                                data-testid={`check-${p.platform}-${i}`}
                                                className="py-2 flex items-start gap-3"
                                            >
                                                <span className="mt-0.5">{STATUS_ICONS[c.status]}</span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm text-slate-100">{c.item}</div>
                                                    <div className="text-xs text-slate-500">{c.note}</div>
                                                </div>
                                                {c.fixable && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        data-testid={`fix-${p.platform}-${c.fix_kind}-btn`}
                                                        onClick={() => fixWithAI(p.platform, c.item, c.fix_kind)}
                                                        disabled={fixBusy === key}
                                                        className="h-7 px-2 border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-100 gap-1"
                                                    >
                                                        {fixBusy === key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                                                        Fix with AI
                                                    </Button>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    })}
                </TabsContent>
            </Tabs>

            {/* Edit dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent data-testid="profile-edit-dialog" className="bg-[#0B0F19] border-white/10 text-slate-100">
                    <DialogHeader><DialogTitle>Edit {editing?.field}</DialogTitle></DialogHeader>
                    <Input
                        data-testid="profile-edit-input"
                        value={editing?.value || ""}
                        onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                        className="bg-white/[0.03] border-white/10 text-slate-100"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)} className="border-white/10 bg-white/[0.03] text-slate-200">Cancel</Button>
                        <Button data-testid="profile-edit-save" onClick={saveEdit} className="bg-indigo-500 hover:bg-indigo-400 text-white">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Rediscover proposals dialog */}
            <Dialog open={!!proposals} onOpenChange={(o) => !o && setProposals(null)}>
                <DialogContent data-testid="rediscover-dialog" className="bg-[#0B0F19] border-white/10 text-slate-100 max-w-2xl">
                    <DialogHeader><DialogTitle>Proposed changes</DialogTitle></DialogHeader>
                    <p className="text-xs text-slate-400">Nothing is applied until you accept per-field. Confirmed data stays intact.</p>
                    <div className="max-h-96 overflow-y-auto space-y-2">
                        {(proposals?.proposed_changes || []).length === 0 ? (
                            <div className="py-4 text-center text-sm text-slate-500">No changes proposed.</div>
                        ) : (
                            (proposals?.proposed_changes || []).map((p, i) => (
                                <div
                                    key={i}
                                    data-testid={`proposal-${p.field}`}
                                    className="rounded-lg border border-white/10 bg-white/[0.03] p-3"
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-xs font-bold text-slate-100">{p.platform}</span>
                                        <Badge variant="outline" className="border-white/10 text-[10px]">{p.confidence}</Badge>
                                    </div>
                                    <div className="text-xs text-slate-500">Current: <span className="text-slate-300">{p.current || "(none)"}</span></div>
                                    <div className="text-xs text-slate-500">Proposed: <span className="text-emerald-300 break-all">{p.proposed}</span></div>
                                    {p.reason && <div className="text-[11px] text-slate-500 mt-1">{p.reason}</div>}
                                    <div className="mt-2 flex gap-2">
                                        <Button
                                            size="sm"
                                            data-testid={`accept-proposal-${p.field}`}
                                            onClick={() => acceptProposal(p)}
                                            className="h-7 bg-emerald-500/15 border border-emerald-500/40 text-emerald-100 hover:bg-emerald-500/25 gap-1"
                                        >
                                            <CheckCircle2 className="h-3 w-3" /> Accept
                                        </Button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function TriState({ label, active, testid }) {
    return (
        <span
            data-testid={testid}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${
                active
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-100"
                    : "border-slate-500/30 bg-slate-500/10 text-slate-400"
            }`}
        >
            {active ? "✓" : "✕"} {label}
        </span>
    );
}
